import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CostReviewRequestDialog } from '../../dialogs/common/cost-review-request/cost-review-request.component';
import { CostReviewCancelDialog } from '../../dialogs/common/cost-review-cancel/cost-review-cancel.component';
import { CostReviewUnlockComponent } from '../../dialogs/common/cost-review-unlock/cost-review-unlock.component';
import { ReviewerCommentsDialogComponent, ReviewerCommentsDialogData } from '../../dialogs/common/reviewer-comments-dialog/reviewer-comments-dialog.component';
import { RefreshReviewRequest, CostReviewerDetails, CostReviewStatus, CostReviewOperationResult, CostReviewDatabaseRecord, ReviewerTypeItem, ForcePublishDetails, CollaborationHistoryEnum, NewCostReviewAttributes_SOL } from '../../../models/dtos';
import { EMPTY, Subject, switchMap, take, takeUntil } from 'rxjs';
import { GetReviewDetailsService } from '../../../services/api/get-review-details.service';
import { ICostReviewActionsDialogReturnValue } from '../../../models/reports/ICostReviewActionsDialogReturnValue';
import { CommonDataService } from '../../../services/common-data.service';
import { NewCostReviewPopulateService } from '../../../services/review/new-cost-review-populate.service';
import { CONSTANTS } from '../../../Constants/Constants';
import { CostReviewRecordBuilderService } from '../../../services/review/cost-review-record-builder.service';
import { TrackChangesDirective } from '../../../directives/track-changes.directive';
import { CommonMetaDataService } from '../../../services/common-meta-data.service';
import { GocCollaborationRestrictionHandlerService } from '../../../services/common/goc-collaboration-restriction-handler.service';
import { ReviewDetailTabsService } from 'review-detail-tabs';
import { ConfirmationDialogService } from '../../../services/common/confirmation-dialog.service';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { CostReviewStateSyncService } from '../../../services/review/cost-review-state-sync.service';
import { CostReviewFunctionsService } from '../../../services/review/cost-review-functions.service';

interface IRefreshedCostReviewState {
  costReviewStatus: CostReviewStatus;
  costReviewers?: CostReviewerDetails[] | null;
}

@Component({
  selector: 'app-review-header',
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatTableModule,
    MatMenuModule,
    TranslatePipe,
    ReactiveFormsModule,
    TrackChangesDirective
  ],
  templateUrl: './review-header.component.html',
  styleUrl: './review-header.component.scss'
})
export class ReviewHeaderComponent implements OnInit, OnDestroy{

  @Input() actionNeededVisible: boolean = false;
  @Input() readOnlyView: boolean = false;
  @Output() readonly openActionNeededDialog = new EventEmitter<void>();

  /** Which primary button to show: 'request' | 'cancel' | 'unlock' */
  crButtonMode: 'request' | 'cancel' | 'unlock' = 'request';
  descriptionControl = new FormControl<string>(CONSTANTS.EMPTY_STRING);
  selectionName: string = '';
  salesPersonName: string = '';
  reviewerName: string = '';
  dateApproved: string = 'N/A';
  private readonly BUSINESS_SELECTION_MAP: ReadonlyMap<string, string> = new Map([
    ['A', 'CHECKLIST_AUTOMATION'],
    ['M', 'CHECKLIST_MECHANICAL'],
    ['S', 'CHECKLIST_SECURITY'],
    ['F', 'CHECKLIST_FIRE'],
    ['E', 'CHECKLIST_ELECTRICAL'],
  ]);

  costReviewStatus: string = 'COST_REVIEW_STATUS_NOT_STARTED';
  autoUnlockMessage: string = '';
  isDeniedWithComments: boolean = false;
  isReadOnly: boolean = false;
  reviewerInfoColumns: string[] = ['role', 'reviewer', 'status'];
  reviewerInfoList: { role: string; reviewer: string; status: string, date: string }[] = [];
  private costReviewData!: CostReviewDatabaseRecord;
  destroy$ = new Subject<void>();

  constructor(private readonly dialog: MatDialog,
    private readonly getReviewDetailsService: GetReviewDetailsService,
    private readonly costReviewRecordBuilderService: CostReviewRecordBuilderService,
    private readonly commonDataService: CommonDataService,
    private readonly newCostReviewPopulateService: NewCostReviewPopulateService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly reviewDetailTabsService: ReviewDetailTabsService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly translateService: TranslateService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly costReviewStateSyncService: CostReviewStateSyncService,
    private readonly costReviewFunctionsService: CostReviewFunctionsService
  ) { }

  ngOnInit(): void {
    this.commonDataService.readOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncLibraryReadOnlyMode());
    this.initializeHeaderData();
    this.subscribeToAutoUnlockChanges();
  }

  /**
   * Recomputes the combined library read-only value from the app flag
   * (`commonDataService.isReadOnlyMode()`) OR the cost-review status
   * (`Approved / InProcess / Pending / None` → read; `NotStarted / Denied`
   * → write) and pushes it to the library via the new single-flag API.
   * Also mirrors the value into local header UI state (readOnlyView and
   * descriptionControl enable/disable).
   */
  private syncLibraryReadOnlyMode(): void {
    const appReadOnly = this.commonDataService.isReadOnlyMode();
    this.isReadOnly = appReadOnly;
    const status = this.costReviewData?.status;
    const isStatusReadOnly = status !== undefined && status !== null
      && status !== CostReviewStatus.NotStarted
      && status !== CostReviewStatus.Denied && status !== CostReviewStatus.None;
    const combined = appReadOnly || isStatusReadOnly;
    this.readOnlyView = combined;
    if (combined) {
      this.descriptionControl.disable();
    } else {
      this.descriptionControl.enable();
    }
    this.reviewDetailTabsService.setReviewLibraryDisableFlag(isStatusReadOnly);
    this.reviewDetailTabsService.setReadOnlyMode(combined);
  }

  private subscribeToAutoUnlockChanges(): void {
    this.costReviewRecordBuilderService.autoUnlockState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.autoUnlockMessage = state.message;
        if (state.statusTranslationKey) {
          this.costReviewStatus = state.statusTranslationKey;
        }
      });
  }

  private initializeHeaderData(): void {
    this.costReviewData = this.costReviewRecordBuilderService.costReviewTableData;
    this.selectionName = this.getSectionName();
    this.salesPersonName = this.resolveSalesPersonName();
    if (this.costReviewData) {
      this.descriptionControl.setValue(this.costReviewData.description || CONSTANTS.EMPTY_STRING);
      this.reviewerName = this.costReviewData.status === CostReviewStatus.NotStarted ? '' : this.getReviewerName();
      this.dateApproved = this.costReviewData.status === CostReviewStatus.Approved ? this.getDateApproved() : CONSTANTS.DEFAULT_NOT_APPLICABLE_STRING;
      this.syncLibraryReadOnlyMode();
      this.crButtonMode = this.resolveButtonMode(this.costReviewData.status);
      this.costReviewStatus = this.costReviewRecordBuilderService.mapStatusToTranslationKey(this.costReviewData.status);
      this.isDeniedWithComments = this.costReviewData.status === CostReviewStatus.Denied
        && !!this.costReviewData.reviewerComments;
    }
    this.buildReviewerInfoList();
  }

  private resolveSalesPersonName(): string {
    return this.commonDataService.getReportHeaderData().salesPersonName;
  }

  getReviewerName(): string {
    if (this.costReviewData?.costReviewers?.length === CONSTANTS.ONE) {
      return this.costReviewData.costReviewers[CONSTANTS.ZERO].reviewerName ?? '';
    }
    return this.costReviewData?.reviewerName ?? CONSTANTS.MULTIPLE_REVIEWERS;
  }

  getDateApproved(): string {
    const costReviewers = this.costReviewData.costReviewers;
    const approvedReviewers = costReviewers.filter(r => r.reviewerStatus === CostReviewStatus.Approved);
    if (approvedReviewers.length === 0) {
      return CONSTANTS.DEFAULT_NOT_APPLICABLE_STRING;
    }
    const sorted = [...approvedReviewers].sort((a, b) => {
      const dateA = a.closeDate ? new Date(a.closeDate).getTime() : 0;
      const dateB = b.closeDate ? new Date(b.closeDate).getTime() : 0;
      return dateB - dateA;
    });
    const closeDate = sorted[0]?.closeDate;
    return closeDate ? this.formatDate(new Date(closeDate)) : CONSTANTS.DEFAULT_NOT_APPLICABLE_STRING;
  }

  onDescriptionChange(): void {
    if (this.costReviewData) {
      this.costReviewData.description = this.descriptionControl.value ?? CONSTANTS.EMPTY_STRING;
      this.commonDataService.setCostReviewData(this.costReviewData);
    }
  }

  private resolveButtonMode(status: number | null): 'request' | 'cancel' | 'unlock' {
    switch (status) {
      case CostReviewStatus.Pending:
      case CostReviewStatus.InProcess:
        return 'cancel';
      case CostReviewStatus.Approved:
        return 'unlock';
      default:
        return 'request';
    }
  }

  getSectionName(): string {
    const business = this.commonDataService.getBusiness();
    return business ? (this.BUSINESS_SELECTION_MAP.get(business) ?? CONSTANTS.EMPTY_STRING) : CONSTANTS.EMPTY_STRING;
  }

  clickRequest() {
    if (this.isReadOnly) {
      return;
    }

    this.reviewDetailTabsService.requestReviewFormDataSync();
    this.populateNewCostReview();
    const dialogRef = this.dialog.open(CostReviewRequestDialog, {
      disableClose: true,
      width: '1000px'
    });

    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: ICostReviewActionsDialogReturnValue) => {
        if (result?.isRequested) {
          this.crButtonMode = 'cancel';
          this.costReviewStatus = 'COST_REVIEW_STATUS_IN_PROGRESS';
          this.costReviewData = result.costReviewData;
          this.updateCostReviewStatus(CostReviewStatus.InProcess);
        }
      },
      error: (error) => {
        console.error('Error closing cost review request dialog:', error);
      }
    });
  }

  clickCancel() {
    if (this.isReadOnly) {
      return;
    }

    this.getReviewDetailsService.refreshCostReview(this.buildRefreshReviewRequest())
      .pipe(take(1))
      .pipe(
        switchMap((response) => {
          if (!response) {
            return EMPTY;
          }

          if (response.costReviewStatus === CostReviewStatus.InProcess) {
            this.openCancelDialog();
            return EMPTY;
          }

          if (response.costReviewStatus === CostReviewStatus.Approved
            || response.costReviewStatus === CostReviewStatus.Denied) {
            const warningMessage = response.costReviewStatus === CostReviewStatus.Approved
              ? 'COST_REVIEW_REQUEST_CANCELLATION_STATUS_APPROVED_WARNING_MESSAGE'
              : 'COST_REVIEW_REQUEST_CANCELLATION_STATUS_DENIED_WARNING_MESSAGE';

            return this.confirmationDialogService.confirmWarning(
              warningMessage,
              'WARNING_TITLE',
              '520px',
              true
            ).pipe(
              take(CONSTANTS.ONE),
              switchMap((shouldContinueCancel) => {
                if (shouldContinueCancel) {
                  this.executeDirectCancelReview(response.costReviewers ?? []);
                  return EMPTY;
                }

                this.applyRefreshedReviewState(response);
                return EMPTY;
              })
            );
          }

          this.applyRefreshedReviewState(response);
          return EMPTY;
        })
      )
      .subscribe({
        error: (error) => {
          console.error('Error refreshing cost review before cancel:', error);
        }
      });
  }

  private openCancelDialog(): void {
    const dialogRef = this.dialog.open(CostReviewCancelDialog, {
      disableClose: true,
      width: '500px'
    });
    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: ICostReviewActionsDialogReturnValue) => {
        if (result) {
          this.costReviewData = result.costReviewData;
          if (result.isCancelled && result.updatedCostReviewers.length === 0) {
            this.cancelAllOrUnlockCostReview();
            this.commonDataService.isSaveEnabled = true;
          } else {
            this.clickRefresh();
          }
        }
      },
      error: (error) => {
        console.error('Error closing cost review cancel dialog:', error);
      }
    });
  }

  private executeDirectCancelReview(latestCostReviewers: CostReviewerDetails[]): void {
    if (!this.costReviewData) {
      return;
    }

    const updatedCostReviewData: CostReviewDatabaseRecord = {
      ...this.costReviewData,
      requesterNotes: CONSTANTS.EMPTY_STRING,
      autoSendEmail: false,
      reportOne: this.getDefaultReportSelectionValue(),
      reportTwo: this.getDefaultReportSelectionValue(),
      reportThree: this.getDefaultReportSelectionValue(),
      costReviewers: [],
      ccUser: this.costReviewRecordBuilderService.generateCCUserList(),
      status: CostReviewStatus.NotStarted
    };
    const estimateData = this.commonDataService.getEstimateData();
    const forcePublishDetails = this.buildDirectCancelForcePublishDetails(latestCostReviewers);
    const request = this.costReviewRecordBuilderService.buildCancelOrUnlockRequest(updatedCostReviewData, forcePublishDetails, estimateData);

    this.getReviewDetailsService.cancelOrUnlockCostReview(request, true)
      .pipe(take(1))
      .subscribe({
        next: (cancelResponse) => {
          if (cancelResponse?.result === CostReviewOperationResult.Success) {
            this.costReviewData = updatedCostReviewData;
            this.cancelAllOrUnlockCostReview(this.costReviewRecordBuilderService.retainReviewersForNextRequest(latestCostReviewers));
            this.commonDataService.isSaveEnabled = true;
          }
        },
        error: async (error) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }
          console.error('Error cancelling cost review from review header:', error);
        }
      });
  }

  private buildDirectCancelForcePublishDetails(latestCostReviewers: CostReviewerDetails[]): ForcePublishDetails | null {
    if (!this.commonDataService.isCollaborationEstimate()) {
      return null;
    }

    const reviewerName = latestCostReviewers?.[0]?.reviewerName
      ?? this.getReviewerName()
      ?? CONSTANTS.EMPTY_STRING;

    const modificationNotes = this.translateService.instant('MODIFICATION_NOTES_COST_REVIEW_CANCELLED', { name: reviewerName });

    return {
      forcePublishAdditionalInfo: CONSTANTS.ESTIMATE_FORCE_PUBLISH_COST_REVIEW_CANCEL,
      operation: CollaborationHistoryEnum.CostReviewCancelled,
      modificationNotes,
      estimateCheckoutAdditionalInfo: CONSTANTS.ESTIMATE_ADD_ON_COST_REVIEW_CANCEL,
      isEstimateBeingCheckedOutAfterPublish: true
    };
  }

  private getDefaultReportSelectionValue(): string {
    return String(CONSTANTS.NEGATIVE_ONE_STRING);
  }

  clickActionNeeded() {
    if (this.isReadOnly) {
      return;
    }

    this.openActionNeededDialog.emit();
  }

  clickUnlock() {
    if (this.isReadOnly) {
      return;
    }

    const dialogRef = this.dialog.open(CostReviewUnlockComponent, {
      disableClose: true,
      width: '700px'
    });
    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: ICostReviewActionsDialogReturnValue) => {
        if (result?.isUnlockRequested) {
          const costReviewData = this.costReviewData;
          const estimateData = this.commonDataService.getEstimateData();
          costReviewData.costReviewers = [];
          costReviewData.autoSendEmail = costReviewData?.autoSendEmail ?? false;
          costReviewData.status = CostReviewStatus.NotStarted;
          const forcePublishDetails = this.costReviewRecordBuilderService.buildUnlockForcePublishDetails(false);
          const request = this.costReviewRecordBuilderService.buildCancelOrUnlockRequest(costReviewData, forcePublishDetails, estimateData);
          this.getReviewDetailsService.cancelOrUnlockCostReview(request, true)
            .pipe(take(1))
            .subscribe({
              next: (response) => {
                if (response?.result === CostReviewOperationResult.Success) {
                  this.costReviewData = costReviewData;
                 this.cancelAllOrUnlockCostReview();
                 if (request.isCollaborativeEstimate && response.collaborationHubData) {                 
                  const collaborationHubData = response.collaborationHubData;
                  this.commonFunctionsService.updateCollaborationHubData(collaborationHubData); 
                 }
                }
              },
              error: async (error) => {
                if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
                  return;
                }
                console.error('Error unlocking cost review:', error);
              }
            });
        }
      },
      error: (error) => {
        console.error('Error closing cost review unlock dialog:', error);
      }
    });
  }

  cancelAllOrUnlockCostReview(retainedCostReviewers: CostReviewerDetails[] = []): void {
    this.crButtonMode = 'request';
    this.costReviewStatus = 'COST_REVIEW_STATUS_NOT_STARTED';
    this.reviewerInfoList = [];
    this.reviewerName = '';
    if(retainedCostReviewers.length > CONSTANTS.ZERO) {
      this.costReviewData.reviewObject.reviewerDetailsForRequestPreference = retainedCostReviewers;
    }
    this.updateCostReviewStatus(CostReviewStatus.NotStarted);
    this.syncCostReviewData();
  }

  // The review object kept the estimate snapshot taken when the review was requested; reloading
  // it pulls the project settings as they were last edited back into the cost review object.
  private syncCostReviewData() {
    this.costReviewStateSyncService.loadCostReviewDetails()
      .catch(error => console.error('Failed to refresh cost review details after cancel or unlock:', error));
  }

  ngOnDestroy(): void {
    this.costReviewData = null;
    this.salesPersonName = CONSTANTS.EMPTY_STRING;
    this.selectionName = CONSTANTS.EMPTY_STRING;
    this.reviewerName = CONSTANTS.EMPTY_STRING;
    this.dateApproved = CONSTANTS.DEFAULT_NOT_APPLICABLE_STRING;
    this.costReviewStatus = 'COST_REVIEW_STATUS_NOT_STARTED';
    this.crButtonMode = 'request';
    this.isDeniedWithComments = false;
    this.destroy$.next();
    this.destroy$.complete();
  }

  clickRefresh() {
    if (this.isReadOnly) {
      return;
    }

    this.getReviewDetailsService.refreshCostReview(this.buildRefreshReviewRequest())
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response) {   
            this.applyRefreshedReviewState(response);
          }
        },
        error: (error) => {
          console.error('Error refreshing cost review:', error);
        }
      });
  }

  private buildRefreshReviewRequest(): RefreshReviewRequest {
    return {
      estimateId: this.commonDataService.getEstimateId() ?? '',
      isCollaborativeEstimate: this.commonDataService.isCollaborationEstimate(),
      version: this.commonDataService.getEstimateMetaData()?.estimateSettings?.version ?? ''
    };
  }

  private applyRefreshedReviewState(response: IRefreshedCostReviewState): void {
    this.updateCostReviewStatus(response.costReviewStatus, response.costReviewers ?? []);
    this.syncCostReviewData();
  }

  private updateCostReviewStatus(status: number, costReviewers?: CostReviewerDetails[] | null): void {
    if (this.costReviewData) {
      this.costReviewData.status = status;
      if (costReviewers !== undefined) {
        this.costReviewData.costReviewers = costReviewers ?? [];
      }
      this.costReviewStatus = this.costReviewRecordBuilderService.mapStatusToTranslationKey(status);
      this.crButtonMode = this.resolveButtonMode(status);
      this.clearReportSelectionsOnReviewCompletion(status);
      this.costReviewRecordBuilderService.costReviewTableData = this.costReviewData;
      this.commonDataService.setCostReviewData(this.costReviewData);
      this.costReviewFunctionsService.refreshReviewTabVisibility();
      this.reviewerName = status === CostReviewStatus.NotStarted ? '' : this.getReviewerName();
      this.dateApproved = status === CostReviewStatus.Approved ? this.getDateApproved() : CONSTANTS.DEFAULT_NOT_APPLICABLE_STRING;
      this.salesPersonName = this.resolveSalesPersonName();
      this.buildReviewerInfoList();
      this.syncLibraryReadOnlyMode();
      this.costReviewRecordBuilderService.resetAutoUnlockState(status);
    }
  }

  /** The requested reports only belong to an open review — drop them once it is closed out. */
  private clearReportSelectionsOnReviewCompletion(status: number): void {
    if (status !== CostReviewStatus.NotStarted && status !== CostReviewStatus.Approved) {
      return;
    }
    this.costReviewData.autoSendEmail = false;
    this.costReviewData.reportOne = this.getDefaultReportSelectionValue();
    this.costReviewData.reportTwo = this.getDefaultReportSelectionValue();
    this.costReviewData.reportThree = this.getDefaultReportSelectionValue();
  }

  populateNewCostReview() {
    this.newCostReviewPopulateService.populateNewCostReviewAttributes().then((newCostReviewAttributes: NewCostReviewAttributes_SOL) => {
      this.costReviewData.newReviewObject = newCostReviewAttributes;
      this.costReviewRecordBuilderService.costReviewTableData = this.costReviewData;
    });
  }

  private buildReviewerInfoList(): void {
    const reviewerDataList = this.costReviewData?.costReviewers || [];
    const reviewersTypesDropdownList: ReviewerTypeItem[] = this.commonMetaDataService.getMetaData().costReviewerTypes ?? [];

    if (reviewerDataList.length > 0) {
      this.reviewerInfoList = reviewerDataList.map(reviewer => ({
        role: reviewersTypesDropdownList.find(r => r.id === reviewer.reviewerTypeID)?.description || '',
        reviewer: reviewer.reviewerName || '',
        status: this.costReviewRecordBuilderService.mapStatusToTranslationKey(reviewer.reviewerStatus),
        date: this.getClosedDate(reviewer)
      }));
    }
  }

  private getClosedDate(reviewer: CostReviewerDetails): string {
    if (reviewer.reviewerStatus === CostReviewStatus.Approved || reviewer.reviewerStatus === CostReviewStatus.Denied) {
      return reviewer.closeDate ? this.formatDate(new Date(reviewer.closeDate)) : CONSTANTS.EMPTY_STRING;
    } else {
      return reviewer.requiredBy ? this.formatDate(new Date(reviewer.requiredBy)) : CONSTANTS.EMPTY_STRING;
    }
  }

  private formatDate(date: Date): string {
    if(!date) {
      return CONSTANTS.DEFAULT_NOT_APPLICABLE_STRING;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return ` ${month}/${day}/${year}`;
  }

  openDeniedNotes(): void {
    const deniedReviewers = this.costReviewData.costReviewers.filter(r => r.reviewerStatus === CostReviewStatus.Denied && !!r.reviewerComments);
    if (deniedReviewers.length > 0) {
      const comments = deniedReviewers.map(r => `${r.reviewerName}: ${r.reviewerComments}`).join('\n');
      this.dialog.open(ReviewerCommentsDialogComponent, {
        width: '500px',
        data: { comments } as ReviewerCommentsDialogData,
        disableClose: true
      });
    }
  }
}
